import { ProjectPlanSnapshot } from '../../../../domain/value-object/ProjectPlanSnapshot'
import { ProjectPlanSelectRow } from '../components/dialog/ProjectPlanSelect/model'
import { TicketListSelectRow } from '../components/dialog/TicketListSelect/model'

type FetchSnapshotRows = (
  snapshotUuid: string
) => Promise<ProjectPlanSnapshot[]>

// スナップショットのデータは不変なため snapshotUuid 単位でキャッシュし、選択コンポーネントの
// 再マウントやヘッダーからの参照で同じ全件取得を繰り返さないようにする。
// 再計算のたびに snapshotUuid は新しくなるため、古い分は二度と参照されず保持する必要がない
const cacheByScope = {
  projectPlan: new Map<string, Promise<ProjectPlanSelectRow[]>>(),
  ticketList: new Map<string, Promise<TicketListSelectRow[]>>(),
}

const getOrFetch = <T>(
  cache: Map<string, Promise<T[]>>,
  snapshotUuid: string,
  fetchRows: () => Promise<T[]>
): Promise<T[]> => {
  const cached = cache.get(snapshotUuid)
  if (cached) return cached
  cache.clear()
  const promise = fetchRows().catch(error => {
    cache.delete(snapshotUuid)
    throw error
  })
  cache.set(snapshotUuid, promise)
  return promise
}

export const fetchProjectPlanSelectRows = (
  snapshotUuid: string,
  fetchMasterSchedule: FetchSnapshotRows
): Promise<ProjectPlanSelectRow[]> =>
  getOrFetch(cacheByScope.projectPlan, snapshotUuid, async () => {
    const response = await fetchMasterSchedule(snapshotUuid)
    return response.map(resp => ({
      uuid: resp.uuid,
      treeValue: resp.path,
      displayName: resp.wbsItem.displayName,
      type: resp.type,
    }))
  })

export const fetchTicketListSelectRows = (
  snapshotUuid: string,
  fetchMasterScheduleTicketLists: FetchSnapshotRows
): Promise<TicketListSelectRow[]> =>
  getOrFetch(cacheByScope.ticketList, snapshotUuid, async () => {
    const response = await fetchMasterScheduleTicketLists(snapshotUuid)
    // このAPIはチケット管理表行のみを返すため ticketType は常に設定される想定
    return response.map(resp => ({
      uuid: resp.uuid,
      displayName: resp.wbsItem.displayName,
      type: resp.type,
      ticketType: resp.wbsItem.ticketType ?? '',
      path: resp.path,
    }))
  })
